import { Fragment } from 'react';
import { Layout as AntLayout, Button, Spin, Tooltip } from 'antd';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { HEADER_HEIGHT, SIDER_WIDTH, SIDER_WIDTH_COLLAPSE } from '../../../constants/layout';
import { ROUTER } from '../../../constants/router';
// import { useLanguage } from '../../../contexts/LanguageContext';
import logoSmall from '../../../assets/hoki-shop.png';
import { cn } from '../../../lib/utils';
import { useTranslation } from 'react-i18next';
import type { MenuItem } from '../types';
import useMenuPermissions from '../hooks/useMenuPermission';

const { Sider } = AntLayout;

function isItemOrChildActive(pathname: string, item: MenuItem): boolean {
  if (pathname === item.link) return true;
  return item.children?.some((c) => pathname === c.link) ?? false;
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Sidebar component with collapsible menu
 */
export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  //   const { translate } = useLanguage();
  const { t } = useTranslation();
  const { filteredGroups, isCheckingPermissions } = useMenuPermissions();
  const handleMenuClick = (link: string) => {
    navigate(link);
  };

  const renderMenuRow = (item: MenuItem, nested: boolean) => {
    const label = t(item.labelKey);
    const active = nested
      ? location.pathname === item.link
      : isItemOrChildActive(location.pathname, item);

    return (
      <Tooltip title={collapsed ? label : undefined} placement="right">
        <div
          onClick={() => handleMenuClick(item.link)}
          className={cn(
            'mb-1 flex cursor-pointer items-center rounded-lg p-3 transition-colors duration-200',
            active ? 'bg-[#007A55] text-[#FFFFFF]' : 'text-[#FFFFFF] hover:bg-[#BCDAD4]/80',
            collapsed ? 'justify-center' : 'justify-start',
            nested && !collapsed && 'ml-3 border-l border-white/25 pl-3'
          )}
        >
          <span
            className={cn('relative flex size-5 items-center justify-center', !collapsed && 'mr-3')}
          >
            {item.icon}
          </span>
          {!collapsed && <span className="text-sm font-medium">{label}</span>}
        </div>
      </Tooltip>
    );
  };

  const renderMenuGroups = () => {
    return filteredGroups.map((group, groupIndex) => (
      <div key={group.titleKey}>
        {!collapsed && (
          <div className="py-1 text-sm text-[#FFFFFF]/90">
            {t(`sidebar.${group.titleKey.toLowerCase()}`, { defaultValue: group.titleKey })}
          </div>
        )}
        {group.childrenItems && (
          <div className="mb-1">
            {group.childrenItems.map((item) => (
              <Fragment key={item.key}>{renderMenuRow(item, false)}</Fragment>
            ))}
          </div>
        )}
        <div className="mb-1">
          {group.items.map((item) => (
            <Fragment key={item.key}>
              {renderMenuRow(item, false)}
              {item.children?.map((child) => (
                <Fragment key={child.key}>{renderMenuRow(child, true)}</Fragment>
              ))}
            </Fragment>
          ))}
        </div>
        {groupIndex < filteredGroups.length - 1 && <div className="mb-4 " />}
      </div>
    ));
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      breakpoint="lg"
      onBreakpoint={(broken) => {
        if (broken) {
          onToggle();
        }
      }}
      className="fixed! top-0! bottom-0! left-0! z-40! px-2!"
      style={{ backgroundColor: '#009966' }}
      collapsedWidth={SIDER_WIDTH_COLLAPSE}
      width={SIDER_WIDTH}
    >
      <div className="flex h-full flex-col">
        {/* Logo Section */}
        <div
          className="relative flex  items-center "
          style={{ height: HEADER_HEIGHT }}
        >
          <Link
            to={ROUTER.DASHBOARD()}
            className={cn(
              'flex w-full items-center gap-2',
              collapsed ? 'justify-center' : 'justify-start'
            )}
          >
            {collapsed ? (
              <img src={logoSmall} alt="Logo" className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8" />
            ) : (
              <div className="flex items-left gap-2 leading-tight">
                <div className="flex flex-col leading-tight px-2">
                  <span className="font-nunito text-xl  font-bold text-[#FFFFFF]">
                    Hoki/Voucher
                  </span>
                </div>
              </div>
            )}
          </Link>

          {/* Toggle Button */}
          <Button
            type="text"
            icon={
              <FontAwesomeIcon
              icon={faChevronRight}
              style={{ fontSize: '16px' }}
              className={cn(
                'text-black transition-transform duration-200',
                collapsed ? 'rotate-0' : 'rotate-180'
              )}
            />
            }
            onClick={onToggle}
            className="absolute! top-1/2 -right-5 flex size-6! -translate-y-1/2 transform items-center justify-center rounded-full! border! bg-white! hover:bg-gray-50!"
            style={{ borderColor: '#BCDAD4' }}
          />
        </div>

        {/* Menu Section */}
        <div
          className={cn(
            'scrollbar-none flex-1 overflow-y-auto text-[#FFFFFF] px-0',
            collapsed ? 'p-3' : 'p-1.5'
          )}
        >
          {isCheckingPermissions ? (
            <div className="flex justify-center py-4">
              <Spin size="small" />
            </div>
          ) : (
            renderMenuGroups()
          )}
        </div>
      </div>
    </Sider>
  );
}
